import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { capitalizeStr } from '../../../../../helpers/formatOrganizationDataForTable';
import { setSyncTimeout } from '../../../../../helpers/setSyncTimeout';

import { COPY_EXCLUDED_ELEMENT_CLASS_NAME } from '../../../../../helpers/handleCopyFormattedText';

import Diagram from '../../../../Diagram/Diagram';
import PlotView from '../../../../PlotView/PlotView';
import EditLineIcon from '../../../../Icons/EditLineIcon';
import { ButtonIcon } from '../../../../index';
import { ErrorWarningLineIcon } from '../../../../Icons';

const PreviewMode = ({ language, code, openEditMode }) => {
    const [errorType, setErrorType] = useState(() => {
        if (!code) return 'empty-code';
        if (language === 'plotly') {
            try {
                JSON.parse(code);
                return null;
            } catch (e) {
                return 'invalid-json';
            }
        }
        return null;
    });

    useEffect(() => {
        let stopTimeout;
        if (errorType) {
            stopTimeout = setSyncTimeout(openEditMode, 3000);
        }

        return () => {
            if (stopTimeout) {
                stopTimeout();
            }
        };
    }, [errorType]);

    const onError = () => setErrorType('invalid-syntax');

    let preview = null;

    if (errorType) {
        let errorMessage;
        switch (errorType) {
            case 'invalid-json':
                errorMessage = 'Invalid JSON format.';
                break;
            case 'invalid-syntax':
                errorMessage = `Invalid ${capitalizeStr(language)} syntax.`;
                break;
            case 'empty-code':
                errorMessage = 'Code is empty.';
                break;
            default:
                errorMessage = `Invalid ${capitalizeStr(language)} syntax.`;
        }
        const additionalMessage =
            errorType === 'empty-code'
                ? 'Add content to generate a preview.'
                : 'Please check your code.';

        preview = (
            <div className="h-[70px] flex items-center justify-center">
                <div className="flex items-start justify-center gap-2">
                    <div className="min-w-fit">
                        <ErrorWarningLineIcon width={32} height={32} color="#E95B69" />
                    </div>
                    <div className="text-red-500">
                        <p className="!font-body-bold !text-body-bold-m !mb-0.5">{errorMessage}</p>
                        <p className="font-body !text-body-regular-xs">{additionalMessage}</p>
                    </div>
                </div>
            </div>
        );
    } else {
        if (language === 'mermaid') {
            preview = (
                <Diagram
                    code={code}
                    name="Diagram Preview"
                    withActionButtons={false}
                    onError={onError}
                    customClassName="hide-default-error-banner"
                />
            );
        }

        if (language === 'plotly') {
            preview = (
                <PlotView
                    value={code}
                    outlined
                    onError={onError}
                    customClassName="!border-neutral-200"
                />
            );
        }
    }

    const containerClassName = classNames('relative mb-3 group', COPY_EXCLUDED_ELEMENT_CLASS_NAME, {
        'min-h-[50px]': language !== 'mermaid',
    });

    return (
        <div className={containerClassName}>
            {preview}

            <div className="absolute top-[6px] right-[10px] opacity-0 group-hover:opacity-100 transition-opacity">
                <ButtonIcon type="neutral" size="xs" icon={EditLineIcon} onClick={openEditMode} />
            </div>
        </div>
    );
};

export default PreviewMode;
